import { useState, useEffect } from "react";
import { auth, db } from "../firebase/config";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { clearAIConfigCache } from "../../services/aiConfigService";
import { SidebarProvider } from "@/components/ui/sidebar";
import OwnerSidebar from "./OwnerSidebar";
import OwnerHeader from "./OwnerHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, Save, RefreshCw, Sparkles, BookOpen, FileText, MessageSquare, AlertCircle, PauseCircle, PlayCircle, Plus, X, AlertTriangle, CheckCircle, Info, Building, Search, Mic, Camera } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Available AI models (Updated December 2024)
const DEFAULT_MODELS = [
  // Gemini 2.5 Series (Latest & Recommended)
  { value: "gemini-2.5-flash-preview-05-20", label: "Gemini 2.5 Flash Preview (Latest)" },
  { value: "gemini-2.5-pro-preview-05-06", label: "Gemini 2.5 Pro Preview (Advanced)" },
  // Gemini 2.0 Series (Stable)
  { value: "gemini-2.0-flash", label: "Gemini 2.0 Flash (Recommended)" },
  { value: "gemini-2.0-flash-lite", label: "Gemini 2.0 Flash-Lite (Fast & Efficient)" },
  { value: "gemini-2.0-flash-thinking-exp-01-21", label: "Gemini 2.0 Flash Thinking (Reasoning)" },
  // Gemini 1.5 Series (Stable)
  { value: "gemini-1.5-flash", label: "Gemini 1.5 Flash (Stable)" },
  { value: "gemini-1.5-flash-8b", label: "Gemini 1.5 Flash-8B (Lightweight)" },
  { value: "gemini-1.5-pro", label: "Gemini 1.5 Pro (Stable)" },
];

// AI features that can be configured
interface AIFeatureConfig {
  research: string;
  chat: string;
  libraryManagement: string;
  examGeneration: string;
  autoRemarks: string;
  navigationSearch: string;
  admissionPattern: string; // New: AI Pattern Creator for Admission Number Setup
  audioAssistant: string; // New: Multimodal Audio Assistant
  independent_research?: string;
  independent_chat?: string;
  independent_libraryManagement?: string;
  independent_examGeneration?: string;
  independent_autoRemarks?: string;
  independent_navigationSearch?: string;
  independent_admissionPattern?: string;
  independent_audioAssistant?: string;
  independent_visionAssistant?: string; // Vision/image analysis for Quick Search image upload
  fallback?: string;
  independent_fallback?: string;
  isPaused?: boolean;
  customModels?: string[];
  pausedSchools?: string[];
}

const DEFAULT_CONFIG: AIFeatureConfig = {
  research: "gemini-2.5-flash-preview-05-20",
  chat: "gemini-2.5-flash-preview-05-20",
  libraryManagement: "gemini-2.5-flash-preview-05-20",
  examGeneration: "gemini-2.5-flash-preview-05-20",
  autoRemarks: "gemini-2.5-flash-preview-05-20",
  navigationSearch: "gemini-2.5-flash-preview-05-20",
  admissionPattern: "gemini-2.5-flash-preview-05-20", 
  audioAssistant: "gemini-2.5-flash-preview-05-20", // New: Default for Audio
  independent_research: "gemini-2.5-flash-preview-05-20",
  independent_chat: "gemini-2.5-flash-preview-05-20",
  independent_libraryManagement: "gemini-2.5-flash-preview-05-20",
  independent_examGeneration: "gemini-2.5-flash-preview-05-20",
  independent_autoRemarks: "gemini-2.5-flash-preview-05-20",
  independent_navigationSearch: "gemini-2.5-flash-preview-05-20",
  independent_admissionPattern: "gemini-2.5-flash-preview-05-20",
  independent_audioAssistant: "gemini-2.5-flash-preview-05-20",
  independent_visionAssistant: "gemini-2.5-flash-preview-05-20", // Must support vision/multimodal
  fallback: "gemini-1.5-flash",
  independent_fallback: "gemini-1.5-flash",
  isPaused: false,
  customModels: [],
  pausedSchools: [],
};




const AIControl = () => {
  const [config, setConfig] = useState<AIFeatureConfig>(DEFAULT_CONFIG);
  const [availableModels, setAvailableModels] = useState(DEFAULT_MODELS);
  const [newModelName, setNewModelName] = useState("");
  const [showAddModel, setShowAddModel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Per-school AI control state
  const [schools, setSchools] = useState<{ id: string; name: string; isPaused: boolean }[]>([]);
  const [schoolSearchQuery, setSchoolSearchQuery] = useState("");
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [togglingSchool, setTogglingSchool] = useState<string | null>(null);

  // AI Usage Analytics state
  const [aiUsageData, setAiUsageData] = useState<{
    schoolId: string;
    schoolName: string;
    totalRequests: number;
    byFeature: { research: number; chat: number; library: number; exams: number };
    totalTokens: number;
    estimatedCost: number;
  }[]>([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Load current configuration from Firestore (after Auth is ready)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        showMessage("error", "You must be signed in to access AI Control.");
        return;
      }

      try {
        // Force refresh the token to get latest claims
        const idTokenResult = await user.getIdTokenResult(true);
        const claims = idTokenResult.claims;

        // Check if user has owner role
        const affiliations = (claims.affiliations as any[]) || [];
        const defaultAff = claims.defaultAffiliation as any;
        
        const hasOwnerRole = defaultAff?.role === "owner" || 
                            affiliations.some((aff: any) => aff.role === "owner");

        if (!hasOwnerRole) {
          setLoading(false);
          showMessage("error", "Access denied. You don't have owner permissions.");
          return;
        }

        setIsOwner(true);
        setCurrentUser(user);
        await loadConfiguration();
      } catch (error) {
        console.error("Error verifying owner access:", error);
        setLoading(false);
        showMessage("error", "Failed to verify access. Please try again.");
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update available models when custom models change
  useEffect(() => {
    const customModelsList = config.customModels || [];
    const customModelsFormatted = customModelsList.map(m => ({ value: m, label: m }));
    setAvailableModels([...DEFAULT_MODELS, ...customModelsFormatted]);
  }, [config.customModels]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);

      const configRef = doc(db, "systemConfig", "aiModels");
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        setConfig({ ...DEFAULT_CONFIG, ...configSnap.data() });
      } else {
        // Document doesn't exist, use default config
        // User can create it by saving
        setConfig(DEFAULT_CONFIG);
        showMessage("error", "AI configuration not found. Using defaults. Click 'Save Changes' to initialize.");
      }
    } catch (error) {
      console.error("Error loading AI configuration:", error);
      const errorCode = (error as any)?.code;
      if (errorCode === "permission-denied") {
        showMessage(
          "error",
          "Permission denied reading AI config. Confirm you're logged in to the correct Firebase project and that Firestore rules are deployed."
        );
      } else {
        showMessage("error", "Failed to load AI configuration. Using defaults.");
      }
      // Use default config as fallback
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  // Load all schools and their AI pause status
  const loadSchools = async () => {
    try {
      setLoadingSchools(true);
      
      // Get all schools
      const schoolsCollection = collection(db, "schools");
      const schoolSnapshot = await getDocs(schoolsCollection);
      
      // Get paused schools list from current config (or reload it)
      // We'll verify against Firestore to be sure
      const configRef = doc(db, "systemConfig", "aiModels");
      const configSnap = await getDoc(configRef);
      const pausedSchoolIds: string[] = configSnap.exists() 
        ? configSnap.data().pausedSchools || [] 
        : [];
      
      const schoolList: { id: string; name: string; isPaused: boolean }[] = [];
      
      for (const docSnap of schoolSnapshot.docs) {
        const schoolId = docSnap.id;
        try {
          // Get school details
          const detailsCollection = collection(db, "schools", schoolId, "schoolDetails");
          const detailsQuery = await getDocs(detailsCollection);
          
          if (!detailsQuery.empty) {
            const data = detailsQuery.docs[0].data();
            schoolList.push({
              id: schoolId,
              name: data.name || "Unnamed School",
              isPaused: pausedSchoolIds.includes(schoolId)
            });
          } else {
            // No schoolDetails, still include with ID as name
            schoolList.push({
              id: schoolId,
              name: schoolId,
              isPaused: pausedSchoolIds.includes(schoolId)
            });
          }
        } catch (err) {
          console.warn(`Could not fetch details for school ${schoolId}`);
        }
      }
      
      // Sort alphabetically by name
      schoolList.sort((a, b) => a.name.localeCompare(b.name));
      setSchools(schoolList);
      
    } catch (error) {
      console.error("Error loading schools:", error);
      showMessage("error", "Failed to load schools list.");
    } finally {
      setLoadingSchools(false);
    }
  };

  // Toggle AI for a specific school
  const handleToggleSchoolAI = async (schoolId: string, currentlyPaused: boolean) => {
    if (!isOwner || !currentUser) {
      showMessage("error", "You must be an authenticated owner to modify school settings.");
      return;
    }
    
    setTogglingSchool(schoolId);
    
    try {
      // We update the main AI config document now
      const configRef = doc(db, "systemConfig", "aiModels");
      const configSnap = await getDoc(configRef);
      
      let currentConfig = configSnap.exists() 
        ? configSnap.data() as AIFeatureConfig
        : DEFAULT_CONFIG;
        
      let pausedSchoolIds = currentConfig.pausedSchools || [];
      
      if (currentlyPaused) {
        // Resume AI for this school - remove from paused list
        pausedSchoolIds = pausedSchoolIds.filter(id => id !== schoolId);
      } else {
        // Pause AI for this school - add to paused list
        if (!pausedSchoolIds.includes(schoolId)) {
          pausedSchoolIds.push(schoolId);
        }
      }
      
      const updatedConfig = { ...currentConfig, pausedSchools: pausedSchoolIds };
      await setDoc(configRef, updatedConfig);
      
      // Update local state
      setConfig(updatedConfig); // Sync local config state
      
      setSchools(prev => prev.map(school => 
        school.id === schoolId 
          ? { ...school, isPaused: !currentlyPaused }
          : school
      ));
      
      // Clear cache so changes take effect immediately
      clearAIConfigCache();
      
      const schoolName = schools.find(s => s.id === schoolId)?.name || schoolId;
      showMessage(
        "success", 
        currentlyPaused 
          ? `AI features resumed for ${schoolName}` 
          : `AI features paused for ${schoolName}`
      );
      
    } catch (error) {
      console.error("Error toggling school AI:", error);
      showMessage("error", "Failed to update school AI settings.");
    } finally {
      setTogglingSchool(null);
    }
  };

  // Load AI Usage Analytics
  const loadAIUsageAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      
      // Check if aiUsageLogs collection exists and fetch data
      const usageLogsRef = collection(db, "aiUsageLogs");
      const logsSnapshot = await getDocs(usageLogsRef);
      
      if (logsSnapshot.empty) {
        setAiUsageData([]);
        return;
      }
      
      // Aggregate usage by school
      const usageBySchool = new Map<string, {
        schoolId: string;
        schoolName: string;
        totalRequests: number;
        byFeature: { research: number; chat: number; library: number; exams: number };
        totalTokens: number;
        estimatedCost: number;
      }>();
      
      logsSnapshot.forEach(doc => {
        const data = doc.data();
        const schoolId = data.schoolId;
        const feature = data.feature || 'chat';
        const tokens = data.totalTokens || 0;  // Fixed: was 'tokens'
        const cost = data.estimatedCost || 0;  // Fixed: was 'cost'
        
        if (!usageBySchool.has(schoolId)) {
          usageBySchool.set(schoolId, {
            schoolId,
            schoolName: data.schoolName || schoolId,
            totalRequests: 0,
            byFeature: { research: 0, chat: 0, library: 0, exams: 0 },
            totalTokens: 0,
            estimatedCost: 0
          });
        }
        
        const schoolData = usageBySchool.get(schoolId)!;
        schoolData.totalRequests++;
        schoolData.totalTokens += tokens;
        schoolData.estimatedCost += cost;
        
        if (feature === 'research') schoolData.byFeature.research++;
        else if (feature === 'chat') schoolData.byFeature.chat++;
        else if (feature === 'library' || feature === 'libraryManagement') schoolData.byFeature.library++;
        else if (feature === 'exams' || feature === 'examGeneration') schoolData.byFeature.exams++;
      });
      
      // Convert to array and sort by total requests
      const sortedData = Array.from(usageBySchool.values())
        .sort((a, b) => b.totalRequests - a.totalRequests);
      
      setAiUsageData(sortedData);
      
    } catch (error) {
      console.error("Error loading AI usage analytics:", error);
      showMessage("error", "Failed to load AI usage analytics.");
      setAiUsageData([]);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Load schools when owner is verified
  useEffect(() => {
    if (isOwner && currentUser) {
      loadSchools();
      loadAIUsageAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, currentUser]);

  // Filter schools by search query
  const filteredSchools = schools.filter(school => 
    school.name.toLowerCase().includes(schoolSearchQuery.toLowerCase()) ||
    school.id.toLowerCase().includes(schoolSearchQuery.toLowerCase())
  );
  const handleSave = async () => {
    if (!isOwner || !currentUser) {
      showMessage("error", "You must be an authenticated owner to save changes.");
      return;
    }

    try {
      setSaving(true);
      const configRef = doc(db, "systemConfig", "aiModels");
      await setDoc(configRef, config);
      
      // Clear the cache so new configuration is used immediately
      clearAIConfigCache();
      
      showMessage("success", "AI models configuration saved successfully! Changes will take effect immediately.");
    } catch (error) {
      console.error("Error saving AI configuration:", error);
      showMessage("error", "Failed to save AI configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULT_CONFIG);
    showMessage("success", "Configuration reset to defaults");
  };

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleModelChange = (feature: keyof AIFeatureConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [feature]: value }));
  };

  const handlePauseToggle = () => {
    // Always show confirmation dialog for both pause and resume
    setShowPauseDialog(true);
  };

  const handleConfirmPause = async (pause: boolean) => {
    if (!isOwner || !currentUser) {
      showMessage("error", "You must be an authenticated owner to pause/resume AI features.");
      setShowPauseDialog(false);
      return;
    }

    try {
      setSaving(true);

      const newConfig = { ...config, isPaused: pause };
      
      const configRef = doc(db, "systemConfig", "aiModels");
      await setDoc(configRef, newConfig);

      // Only update local state after Firestore write succeeds
      setConfig(newConfig);
      
      // Clear the cache so changes take effect immediately
      clearAIConfigCache();
      
      showMessage(
        "success",
        pause
          ? "AI features paused globally. All schools are now unable to use AI features."
          : "AI features resumed. All schools can now use AI features again."
      );
    } catch (error) {
      console.error("Error updating pause state:", error);
      const errorCode = (error as any)?.code;
      if (errorCode === "permission-denied") {
        showMessage(
          "error",
          "Permission denied. Your account is not recognized as an Owner in Firestore rules. Run update-owner-claims.js, then sign out/in (or hard refresh) to refresh your token."
        );
      } else {
        showMessage("error", "Failed to update AI pause state");
      }
    } finally {
      setSaving(false);
      setShowPauseDialog(false);
    }
  };

  const handleAddCustomModel = () => {
    if (!newModelName.trim()) {
      showMessage("error", "Please enter a model name");
      return;
    }
    
    const customModels = config.customModels || [];
    if (customModels.includes(newModelName.trim())) {
      showMessage("error", "Model already exists");
      return;
    }

    setConfig((prev) => ({
      ...prev,
      customModels: [...(prev.customModels || []), newModelName.trim()],
    }));
    setNewModelName("");
    setShowAddModel(false);
    showMessage("success", "Custom model added. Don't forget to save!");
  };

  const handleRemoveCustomModel = (modelName: string) => {
    setConfig((prev) => ({
      ...prev,
      customModels: (prev.customModels || []).filter(m => m !== modelName),
    }));
    showMessage("success", "Custom model removed. Don't forget to save!");
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <OwnerSidebar userRole="owner" activeSection="" onSectionChange={() => {}} />
          <div className="flex-1 flex flex-col overflow-hidden">
            <OwnerHeader />
            <main className="flex-1 overflow-y-auto">
              <div className="flex items-center justify-center h-96">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <OwnerSidebar userRole="owner" activeSection="" onSectionChange={() => {}} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <OwnerHeader />
          <main className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              AI Model Control
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Configure AI models for different features
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={saving}
            className="gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2 bg-[#155DFC] hover:bg-[#1148CA] text-white"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <Alert className={message.type === "success" ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-red-500 bg-red-50 dark:bg-red-900/20"}>
          <AlertCircle className={`h-4 w-4 ${message.type === "success" ? "text-green-600" : "text-red-600"}`} />
          <AlertDescription className={message.type === "success" ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Global Pause Toggle */}
      <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {config.isPaused ? (
                <PlayCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              ) : (
                <PauseCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              )}
              <div>
                <h3 className="font-semibold text-yellow-900 dark:text-yellow-100">
                  {config.isPaused ? "AI Features Paused" : "AI Features Active"}
                </h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {config.isPaused
                    ? "All AI features are currently paused for all schools. Enable to restore functionality."
                    : "AI features are active across all schools. Toggle to pause all AI features globally."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                {config.isPaused ? "Paused" : "Active"}
              </span>
              <Switch
                checked={!config.isPaused}
                onCheckedChange={handlePauseToggle}
                className="data-[state=checked]:bg-green-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-School AI Control */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
                <Building className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle>Per-School AI Control</CardTitle>
                <CardDescription>
                  Enable or disable AI features for specific schools
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadSchools}
              disabled={loadingSchools}
              className="gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <RefreshCw className={`h-4 w-4 ${loadingSchools ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 m-2">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search schools by name or ID..."
              value={schoolSearchQuery}
              onChange={(e) => setSchoolSearchQuery(e.target.value)}
              className="pl-10 bg-white dark:bg-gray-950"
            />
          </div>

          {/* Schools List */}
          {loadingSchools ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-indigo-500" />
              <span className="ml-2 text-gray-500">Loading schools...</span>
            </div>
          ) : schools.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
              <Building className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No schools found in the system.
              </p>
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <Search className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No schools match "{schoolSearchQuery}"
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto pr-2 py-1">
              {filteredSchools.map((school) => (
                <div
                  key={school.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${
                    school.isPaused 
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  } hover:shadow-md`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg ${
                      school.isPaused 
                        ? 'bg-red-100 dark:bg-red-900/30' 
                        : 'bg-green-100 dark:bg-green-900/30'
                    }`}>
                      <Building className={`h-4 w-4 ${
                        school.isPaused 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-green-600 dark:text-green-400'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {school.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        ID: {school.id}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      school.isPaused 
                        ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' 
                        : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                    }`}>
                      {school.isPaused ? 'AI Paused' : 'AI Active'}
                    </span>
                    <Switch
                      checked={!school.isPaused}
                      onCheckedChange={() => handleToggleSchoolAI(school.id, school.isPaused)}
                      disabled={togglingSchool === school.id || config.isPaused}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {schools.length > 0 && (
            <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-medium">{schools.length}</span> total schools
              </div>
              <div className="flex gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-green-500"></span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {schools.filter(s => !s.isPaused).length} Active
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500"></span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {schools.filter(s => s.isPaused).length} Paused
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Warning when global pause is active */}
          {config.isPaused && (
            <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-900/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                Global AI pause is active. Per-school toggles are disabled. Disable global pause to manage individual schools.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* AI Usage Analytics Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>AI Usage Analytics</CardTitle>
              <CardDescription>
                Track AI usage across all schools and features
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadAIUsageAnalytics}
              disabled={loadingAnalytics}
              className="gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {loadingAnalytics ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span>Refresh</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingAnalytics ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-indigo-500" />
              <span className="ml-2 text-gray-500">Loading analytics...</span>
            </div>
          ) : aiUsageData.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
              <FileText className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No AI usage data found. Usage will appear here once AI features are used.
              </p>
            </div>
          ) : (
            <>
              {/* Top Schools by Usage */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Top Schools by AI Usage</h3>
                <div className="space-y-3">
                  {aiUsageData.slice(0, 5).map((school, index) => (
                    <div
                      key={school.schoolId}
                      className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50">
                          <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">#{index + 1}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {school.schoolName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {school.totalRequests.toLocaleString()} requests • {school.totalTokens.toLocaleString()} tokens
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                          ${school.estimatedCost.toFixed(2)}
                        </span>
                        <div className="flex gap-2 text-xs">
                          <span className="text-gray-500">R:{school.byFeature.research}</span>
                          <span className="text-gray-500">C:{school.byFeature.chat}</span>
                          <span className="text-gray-500">L:{school.byFeature.library}</span>
                          <span className="text-gray-500">E:{school.byFeature.exams}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-center p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {aiUsageData.reduce((sum, s) => sum + s.totalRequests, 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total Requests</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {aiUsageData.reduce((sum, s) => sum + s.totalTokens, 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total Tokens</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    ${aiUsageData.reduce((sum, s) => sum + s.estimatedCost, 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Estimated Cost</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {aiUsageData.length}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Active Schools</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Dynamic AI Model Configuration</p>
              <p>
                Control which AI model is used for each feature in your application. 
                Changes take effect immediately without requiring code modifications or redeployment.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Models Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Custom AI Models</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAddModel(!showAddModel)}
              className="gap-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors shadow-sm dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800 dark:hover:border-gray-600"
            >
              <Plus className="h-4 w-4" />
              Add New Model
            </Button>
          </CardTitle>
          <CardDescription>
            Add new Google AI models as they become available
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showAddModel && (
            <div className="flex gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 animate-in fade-in slide-in-from-top-2">
              <div className="flex-1">
                <Input
                  placeholder="Enter model name (e.g., gemini-1.5-flash-latest)"
                  value={newModelName}
                  onChange={(e) => setNewModelName(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddCustomModel()}
                  className="bg-white dark:bg-gray-950"
                  autoFocus
                />
              </div>
              <Button onClick={handleAddCustomModel} size="sm" className="bg-[#155DFC] hover:bg-[#1148CA] text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
              <Button onClick={() => setShowAddModel(false)} size="sm" variant="ghost">
                Cancel
              </Button>
            </div>
          )}
          
          {config.customModels && config.customModels.length > 0 ? (
            <div className="space-y-3">
              <Label className="text-sm text-muted-foreground">Active Custom Models</Label>
              <div className="flex flex-wrap gap-3">
                {config.customModels.map((model) => (
                  <div
                    key={model}
                    className="group flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800 transition-all duration-200"
                  >
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {model}
                    </span>
                    <button
                      onClick={() => handleRemoveCustomModel(model)}
                      aria-label={`Remove ${model}`}
                      className="ml-1 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            !showAddModel && (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                <Brain className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No custom models added yet. Add new models to extend capabilities.
                </p>
                <Button 
                  variant="link" 
                  onClick={() => setShowAddModel(true)}
                  className="text-[#155DFC] mt-1"
                >
                  Add your first model
                </Button>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* AI Features Configuration */}
      <Tabs defaultValue="school" className="w-full">
        <TabsList className="mb-6 grid w-full grid-cols-2">
          <TabsTrigger value="school">School Platform Features</TabsTrigger>
          <TabsTrigger value="independent">Independent Workspace Features</TabsTrigger>
        </TabsList>

        <TabsContent value="school">
          <div className="grid gap-6 md:grid-cols-2">
             {/* ... existing cards ... */}
             {/* I will copy the existing cards here but I must be careful not to delete them if I can't recreate them all perfectly. */}
             {/* Actually, the replace tool requires me to provide the REPLACEMENT content. */}
             {/* If I replace the whole grid, I have to provide the whole grid back. */}
             {/* That is 300 lines of code. */}
             {/* It might be safer to INSERT the Tabs start tag BEFORE the grid, and the Tabs end tag AFTER the grid, and then wrap the independent content. */}
             {/* But I can't do "insert around" easily. */}
             {/* I will use a smaller strategy: Rename 'IndependentSearchUtility' related updates first? No that's done. */}
             
             {/* PLAN B: I will replace the START of the grid div with the Tabs structure, and then finding the END of the grid div and appending the other tab content. */}
             {/* BUT if I open an element I must close it in the same replacement or the file is broken between steps. */}
             
             {/* I will take the hit and provide the full content for the School Tab (existing cards) + the new Independent Tab. */}
             {/* Since I have the file content in history, I can copy it. */}


        {/* Research Feature */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <CardTitle>Research & Papers</CardTitle>
                <CardDescription>
                  AI model for research generation and analysis
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="research-model">Select Model</Label>
              <Select
                value={config.research}
                onValueChange={(value) => handleModelChange("research", value)}
              >
                <SelectTrigger id="research-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-purple-500/20 data-[state=open]:border-purple-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Used for generating research papers, citations, and analysis
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Auto Remarks Config */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                <MessageSquare className="h-5 w-5 text-pink-600 dark:text-pink-400" />
              </div>
              <div>
                <CardTitle>Auto Remarks</CardTitle>
                <CardDescription>
                  AI model for generating student report card remarks
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="auto-remarks-model">Select Model</Label>
              <Select
                value={config.autoRemarks}
                onValueChange={(value) => handleModelChange("autoRemarks", value)}
              >
                <SelectTrigger id="auto-remarks-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-pink-500/20 data-[state=open]:border-pink-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Generates unique, themed remarks for student report cards
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Chat Feature */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle>AI Chat</CardTitle>
                <CardDescription>
                  AI model for chatbot interactions
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="chat-model">Select Model</Label>
              <Select
                value={config.chat}
                onValueChange={(value) => handleModelChange("chat", value)}
              >
                <SelectTrigger id="chat-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-blue-500/20 data-[state=open]:border-blue-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Used for student and teacher chatbot conversations
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Library Management Feature */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <BookOpen className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <CardTitle>Library Management</CardTitle>
                <CardDescription>
                  AI model for library and book recommendations
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="library-model">Select Model</Label>
              <Select
                value={config.libraryManagement}
                onValueChange={(value) => handleModelChange("libraryManagement", value)}
              >
                <SelectTrigger id="library-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-green-500/20 data-[state=open]:border-green-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Used for book summaries and recommendations
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Exam Generation Feature */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <CardTitle>Exam Generation</CardTitle>
                <CardDescription>
                  AI model for generating exams and questions
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="exam-model">Select Model</Label>
              <Select
                value={config.examGeneration}
                onValueChange={(value) => handleModelChange("examGeneration", value)}
              >
                <SelectTrigger id="exam-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-orange-500/20 data-[state=open]:border-orange-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Used for generating exam papers and questions
              </p>
            </div>
          </CardContent>
        </Card>


        {/* Navigation Search Feature */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <Search className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <CardTitle>Navigation Search</CardTitle>
                <CardDescription>
                  AI model for interpreting user navigation queries
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="navigation-model">Select Model</Label>
              <Select
                value={config.navigationSearch}
                onValueChange={(value) => handleModelChange("navigationSearch", value)}
              >
                <SelectTrigger id="navigation-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-indigo-500/20 data-[state=open]:border-indigo-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Used for guiding users to the correct page based on intent
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Admission Pattern Feature */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                <Sparkles className="h-5 w-5 text-pink-600 dark:text-pink-400" />
              </div>
              <div>
                <CardTitle>Admission Pattern AI</CardTitle>
                <CardDescription>
                  AI model for generating admission number patterns
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="admission-model">Select Model</Label>
              <Select
                value={config.admissionPattern}
                onValueChange={(value) => handleModelChange("admissionPattern", value)}
              >
                <SelectTrigger id="admission-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-pink-500/20 data-[state=open]:border-pink-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Used to help create admission number patterns via AI wizard
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Audio Assistant Feature */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <Mic className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <CardTitle>Voice & Audio Assistant</CardTitle>
                <CardDescription>
                  Model for speech-to-text and audio reasoning
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="audio-model">Select Model</Label>
              <Select
                value={config.audioAssistant}
                onValueChange={(value) => handleModelChange("audioAssistant", value)}
              >
                <SelectTrigger id="audio-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-red-500/20 data-[state=open]:border-red-500 transition-colors">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem 
                      key={model.value} 
                      value={model.value}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Use Flash models for faster voice response times
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Global Fallback Model */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <AlertCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <CardTitle>Fallback Model</CardTitle>
                <CardDescription>
                  Used globally if the primary model fails
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="fallback-model">Select Fallback Model</Label>
              <Select
                value={config.fallback || "gemini-1.5-flash"}
                onValueChange={(value) => handleModelChange("fallback", value)}
              >
                <SelectTrigger id="fallback-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-gray-500/20 transition-colors">
                  <SelectValue placeholder="Select a fallback model" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                  {availableModels.map((model) => (
                    <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900">
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                A highly reliable model is recommended (e.g. Gemini 1.5 Flash).
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="independent">
          <Card className="border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                Independent Teacher Platform
              </CardTitle>
              <CardDescription>
                Configure AI models for teach.sonoma.school — only features that actually use AI are shown here.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                {/* Research Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <CardTitle>Research & Papers</CardTitle>
                        <CardDescription>
                          Used by the Research page to generate papers, citations, and topic analysis
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-research-model">Select Model</Label>
                      <Select
                        value={config.independent_research || config.research}
                        onValueChange={(value) => handleModelChange("independent_research", value)}
                      >
                        <SelectTrigger id="ind-research-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-purple-500/20 data-[state=open]:border-purple-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Fallback: uses the School Platform "Research" model if not set
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Exam Generation Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div>
                        <CardTitle>Exam Generation</CardTitle>
                        <CardDescription>
                          Used by the Exam Generator to create questions and PDF-based assessments
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-exam-model">Select Model</Label>
                      <Select
                        value={config.independent_examGeneration || config.examGeneration}
                        onValueChange={(value) => handleModelChange("independent_examGeneration", value)}
                      >
                        <SelectTrigger id="ind-exam-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-orange-500/20 data-[state=open]:border-orange-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Fallback: uses the School Platform "Exam Generation" model if not set
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Chat Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <CardTitle>AI Chat</CardTitle>
                        <CardDescription>Used by the AI chatbot / assistant for independent teachers</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-chat-model">Select Model</Label>
                      <Select value={config.independent_chat || config.chat} onValueChange={(value) => handleModelChange("independent_chat", value)}>
                        <SelectTrigger id="ind-chat-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-blue-500/20 data-[state=open]:border-blue-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">{model.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fallback: uses the School Platform "Chat" model if not set</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Library Management Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <BookOpen className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <CardTitle>Library Management</CardTitle>
                        <CardDescription>Used for resource recommendations and library cataloging</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-library-model">Select Model</Label>
                      <Select value={config.independent_libraryManagement || config.libraryManagement} onValueChange={(value) => handleModelChange("independent_libraryManagement", value)}>
                        <SelectTrigger id="ind-library-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-green-500/20 data-[state=open]:border-green-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">{model.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fallback: uses the School Platform "Library Management" model if not set</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Auto Remarks Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                        <MessageSquare className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                      </div>
                      <div>
                        <CardTitle>Auto Remarks</CardTitle>
                        <CardDescription>Used for generating student report card remarks</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-remarks-model">Select Model</Label>
                      <Select value={config.independent_autoRemarks || config.autoRemarks} onValueChange={(value) => handleModelChange("independent_autoRemarks", value)}>
                        <SelectTrigger id="ind-remarks-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-pink-500/20 data-[state=open]:border-pink-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">{model.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fallback: uses the School Platform "Auto Remarks" model if not set</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Navigation Search Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                        <Search className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <CardTitle>Navigation Search</CardTitle>
                        <CardDescription>Used for interpreting navigation queries in the independent platform</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-nav-model">Select Model</Label>
                      <Select value={config.independent_navigationSearch || config.navigationSearch} onValueChange={(value) => handleModelChange("independent_navigationSearch", value)}>
                        <SelectTrigger id="ind-nav-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-indigo-500/20 data-[state=open]:border-indigo-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">{model.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fallback: uses the School Platform "Navigation Search" model if not set</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Admission Pattern Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                        <Sparkles className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                      </div>
                      <div>
                        <CardTitle>Admission Pattern AI</CardTitle>
                        <CardDescription>Used for generating admission number patterns in the independent platform</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-admission-model">Select Model</Label>
                      <Select value={config.independent_admissionPattern || config.admissionPattern} onValueChange={(value) => handleModelChange("independent_admissionPattern", value)}>
                        <SelectTrigger id="ind-admission-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-pink-500/20 data-[state=open]:border-pink-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">{model.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fallback: uses the School Platform "Admission Pattern" model if not set</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Audio Assistant Feature */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                        <Mic className="h-5 w-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <CardTitle>Voice & Audio Assistant</CardTitle>
                        <CardDescription>
                          Handles voice/audio queries in the Quick Search — teacher speaks a question, AI transcribes and responds
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-audio-model">Select Model</Label>
                      <Select
                        value={config.independent_audioAssistant || config.audioAssistant}
                        onValueChange={(value) => handleModelChange("independent_audioAssistant", value)}
                      >
                        <SelectTrigger id="ind-audio-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-red-500/20 data-[state=open]:border-red-500 transition-colors">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ⚡ Use Flash models for faster voice response. Must support audio input (all Gemini 2.0+ models do).
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Vision / Image Analysis Feature (full-width) */}
                <Card className="md:col-span-2 border-violet-200 dark:border-violet-800">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                        <Camera className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          Vision & Image Analysis
                          <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300">Quick Search — Image Upload</span>
                        </CardTitle>
                        <CardDescription>
                          <strong>Place a multimodal model here.</strong> When a teacher photographs a question on the board and uploads it, this model reads the image, identifies the question, and provides step-by-step working + a final answer. Must support vision input (image analysis).
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800 p-3 rounded-lg text-sm text-violet-800 dark:text-violet-200">
                        <p className="font-semibold mb-1">📷 Required Capability: Multimodal Vision</p>
                        <ul className="list-disc ml-5 space-y-1 text-xs">
                          <li>Must be able to <strong>read and analyze image content</strong> (not just text)</li>
                          <li>Recommended: <code>gemini-2.5-flash-preview-05-20</code>, <code>gemini-2.0-flash</code>, or <code>gemini-1.5-pro</code></li>
                          <li>Used when a teacher uploads a photo of a whiteboard, worksheet, or textbook page</li>
                          <li>The model will extract the question from the image and provide a structured solution</li>
                        </ul>
                      </div>
                      <Label htmlFor="ind-vision-model">Select Vision-Capable Model</Label>
                      <Select
                        value={config.independent_visionAssistant || config.independent_audioAssistant || config.audioAssistant}
                        onValueChange={(value) => handleModelChange("independent_visionAssistant", value)}
                      >
                        <SelectTrigger id="ind-vision-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-violet-500/20 data-[state=open]:border-violet-500 transition-colors">
                          <SelectValue placeholder="Select a vision-capable model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900">
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Fallback chain: <code>independent_visionAssistant</code> → <code>independent_audioAssistant</code> → <code>audioAssistant</code>. All Gemini 2.0+ Flash models support vision.
                      </p>
                    </div>
                  </CardContent>
                </Card>
                {/* Independent Fallback Model */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <CardTitle>Independent Fallback Model</CardTitle>
                        <CardDescription>
                          Used globally for the independent platform if the primary model fails
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="ind-fallback-model">Select Fallback Model</Label>
                      <Select
                        value={config.independent_fallback || config.fallback || "gemini-1.5-flash"}
                        onValueChange={(value) => handleModelChange("independent_fallback", value)}
                      >
                        <SelectTrigger id="ind-fallback-model" className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 focus:ring-gray-500/20 transition-colors">
                          <SelectValue placeholder="Select a fallback model" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {availableModels.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 focus:bg-gray-100 dark:focus:bg-gray-900">
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Fallback: uses the School Platform "Fallback" model if not set
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Model Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Model Information</CardTitle>
          <CardDescription>Understanding the different AI models</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="grid gap-2">
              <div className="flex items-start gap-2">
                <div className="font-medium min-w-[180px]">Gemini 2.5 Series:</div>
                <div className="text-gray-600 dark:text-gray-400">Latest preview models with enhanced reasoning. Supports Flash (Fast) and Pro (Advanced).</div>
              </div>
              <div className="flex items-start gap-2">
                <div className="font-medium min-w-[180px]">Gemini 2.0 Series:</div>
                <div className="text-gray-600 dark:text-gray-400">Stable, high-performance models. Flash-Lite is ultra-efficient. Thinking Exp is good for logic.</div>
              </div>
              <div className="flex items-start gap-2">
                <div className="font-medium min-w-[180px]">Gemini 1.5 Series:</div>
                <div className="text-gray-600 dark:text-gray-400">Previous generation stable models. Known for consistent performance.</div>
              </div>
              <div className="flex items-start gap-2">
                <div className="font-medium min-w-[180px]">Custom Models:</div>
                <div className="text-gray-600 dark:text-gray-400">Add new Google AI models as they're released to stay up-to-date.</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
            </div>
          </main>
        </div>
      </div>

      {/* Pause Confirmation Dialog */}
      {/* Pause Confirmation Dialog */}
      <AlertDialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <AlertDialogContent className={`max-w-2xl w-[90vw] max-h-[85vh] overflow-y-auto border-2 ${!config.isPaused ? "border-red-500" : "border-emerald-500"}`}>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-full ${!config.isPaused ? "bg-red-100 dark:bg-red-900/30" : "bg-emerald-100 dark:bg-emerald-900/30"}`}>
                {!config.isPaused ? (
                  <AlertTriangle className={`h-6 w-6 ${!config.isPaused ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`} />
                ) : (
                  <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                )}
              </div>
              <AlertDialogTitle className="text-xl">
                {!config.isPaused ? "Pause All AI Features?" : "Resume All AI Features?"}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base pt-4">
              {!config.isPaused 
                ? "This will immediately disable all AI features across all schools."
                : "This will restore AI capabilities across all schools."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* NOTE: AlertDialogDescription renders a <p>. Keep complex markup outside to avoid invalid nesting. */}
          <div className="space-y-4 text-gray-700 dark:text-gray-300">
            <div className={`font-semibold ${!config.isPaused ? "text-yellow-800 dark:text-yellow-200" : "text-emerald-800 dark:text-emerald-200"}`}>
              {!config.isPaused 
                ? "⚠️ This action will immediately affect all schools using your platform."
                : "✅ All AI features will become available to schools immediately."}
            </div>

            <div className="space-y-3">
              <div className="font-medium">
                {!config.isPaused 
                  ? "The following features will be disabled globally:" 
                  : "The following features will be restored:"}
              </div>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 mt-1 text-gray-500" />
                  <span><strong>AI Chat Assistant</strong> - {!config.isPaused ? "Students and teachers will be unable to use the AI chatbot" : "Chat capabilities will be re-enabled"}</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-4 w-4 mt-1 text-gray-500" />
                  <span><strong>Research Tools</strong> - {!config.isPaused ? "AI-powered research and analysis features will be unavailable" : "Research generation and analysis will be active"}</span>
                </li>
                <li className="flex items-start gap-2">
                  <BookOpen className="h-4 w-4 mt-1 text-gray-500" />
                  <span><strong>Library Management</strong> - {!config.isPaused ? "AI book cataloging and recommendations will stop working" : "Smart cataloging and recommendations will work"}</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-4 w-4 mt-1 text-gray-500" />
                  <span><strong>Exam Generation</strong> - {!config.isPaused ? "Teachers cannot generate AI-assisted exam questions" : "Exam generation tools will be available"}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg border border-gray-300 dark:border-gray-700">
              <div className="font-medium mb-2 flex items-center gap-2">
                <Info className="h-4 w-4" />
                Impact:
              </div>
              <ul className="space-y-1 text-sm ml-6 list-disc">
                {!config.isPaused ? (
                  <>
                    <li>Users will see error messages when trying to use AI features</li>
                    <li>All pending AI operations will fail</li>
                    <li>This change takes effect immediately across all schools</li>
                    <li>You can resume AI features anytime by toggling the switch again</li>
                  </>
                ) : (
                  <>
                    <li>Users will regain access to all AI tools immediately</li>
                    <li>Pending or failed operations can be retried</li>
                    <li>System performance may see increased load</li>
                  </>
                )}
              </ul>
            </div>

            <div className="text-sm text-gray-600 dark:text-gray-400 italic">
              {!config.isPaused 
                ? "Use this feature for maintenance, cost control, or when temporarily disabling AI capabilities is necessary."
                : "Ensure your API quotas are sufficient before resuming services globally."}
            </div>
          </div>

          <AlertDialogFooter className="gap-4 sm:gap-4 button-container">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleConfirmPause(!config.isPaused)}
              className={`${!config.isPaused ? "bg-red-600 hover:bg-red-700 focus:ring-red-600" : "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-600"}`}
            >
              {!config.isPaused ? "Yes, Pause All AI Features" : "Yes, Resume All AI Features"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

export default AIControl;
